import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { getCustomers, getSites, getHeatPumps } from "../lib/sync";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { EntitySelect as Sel } from "../components/EntitySelect";
import { QuickAddDialog } from "../components/QuickAddDialog";
import MapLink, { mapsRouteUrl } from "../components/MapLink";
import { CalendarPlus, User, Trash2, CalendarClock, Route, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const JOB_TYPES = ["Annual Service", "6-Month Service", "Commissioning", "Repair / Callout", "Inspection"];
const DURATIONS = [
  { value: "30", label: "30 min" },
  { value: "45", label: "45 min" },
  { value: "60", label: "1 hr" },
  { value: "90", label: "1.5 hr" },
  { value: "120", label: "2 hr" },
  { value: "180", label: "3 hr" },
  { value: "240", label: "4 hr" },
];
const toMin = (t) => {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
const fmtMin = (mins) => {
  if (mins == null) return "";
  const m = ((mins % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};
const endTimeStr = (j) => {
  const s = toMin(j.start_time);
  if (s == null) return "";
  return fmtMin(s + (Number(j.duration_min) || 0));
};
const jobsOverlap = (a, b) => {
  if (!a.engineer_id || a.engineer_id !== b.engineer_id) return false;
  if (a.scheduled_date !== b.scheduled_date) return false;
  const as = toMin(a.start_time), bs = toMin(b.start_time);
  if (as == null || bs == null) return false;
  const ae = as + (Number(a.duration_min) || 0);
  const be = bs + (Number(b.duration_min) || 0);
  return as < be && bs < ae;
};

export default function Schedule() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ scheduled_date: todayISO(), start_time: "09:00", duration_min: 60, job_type: "Annual Service", status: "scheduled" });
  const [saving, setSaving] = useState(false);
  const [addType, setAddType] = useState(null);
  const [addForm, setAddForm] = useState({});
  const [addSaving, setAddSaving] = useState(false);
  const [routeDate, setRouteDate] = useState(todayISO());

  const loadJobs = () => api.get("/jobs").then(({ data }) => setJobs(data)).catch(() => {});
  useEffect(() => {
    loadJobs();
    getCustomers().then(setCustomers).catch(() => {});
    getSites().then(setSites).catch(() => {});
    getHeatPumps().then(setPumps).catch(() => {});
    api.get("/users").then(({ data }) => setEngineers(data.filter((u) => u.role === "engineer" && u.active))).catch(() => {});
  }, []);

  const filteredSites = sites.filter((s) => s.customer_id === form.customer_id);
  const filteredPumps = pumps.filter((p) => p.site_id === form.site_id);

  const byTime = (a, b) => (a.start_time || "99:99").localeCompare(b.start_time || "99:99");
  const todaysJobs = jobs.filter((j) => j.scheduled_date === routeDate).sort(byTime);
  const todaysRouteUrl = mapsRouteUrl(todaysJobs.map((j) => j.site_address || j.site_name));
  const sortedJobs = [...jobs].sort(
    (a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || "") || byTime(a, b)
  );
  const overlapIds = new Set();
  for (let i = 0; i < jobs.length; i++)
    for (let k = i + 1; k < jobs.length; k++)
      if (jobsOverlap(jobs[i], jobs[k])) {
        overlapIds.add(jobs[i].id);
        overlapIds.add(jobs[k].id);
      }
  const formOverlap =
    form.engineer_id &&
    form.start_time &&
    jobs.some((j) => j.id !== form.id && jobsOverlap(j, form));

  const save = async () => {
    if (!form.customer_id || !form.site_id || !form.engineer_id)
      return toast.error("Select customer, site and engineer");
    setSaving(true);
    try {
      await api.post("/jobs", form);
      toast.success("Job scheduled");
      setOpen(false);
      setForm({ scheduled_date: todayISO(), start_time: "09:00", duration_min: 60, job_type: "Annual Service", status: "scheduled" });
      loadJobs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not schedule");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Job removed");
      loadJobs();
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  const saveAdd = async () => {
    const t = addType;
    if (t === "customer" && !addForm.name) return toast.error("Customer name is required");
    if (t === "site" && !addForm.name) return toast.error("Site name is required");
    setAddSaving(true);
    try {
      if (t === "customer") {
        const { data } = await api.post("/customers", addForm);
        setCustomers(await getCustomers().catch(() => []));
        setForm((f) => ({ ...f, customer_id: data.id, site_id: "", heat_pump_id: "" }));
        toast.success("Customer added");
      } else if (t === "site") {
        const { data } = await api.post("/sites", { ...addForm, customer_id: form.customer_id });
        setSites(await getSites().catch(() => []));
        setForm((f) => ({ ...f, site_id: data.id, heat_pump_id: "" }));
        toast.success("Site added");
      } else if (t === "pump") {
        const { data } = await api.post("/heatpumps", {
          ...addForm,
          customer_id: form.customer_id,
          site_id: form.site_id,
        });
        setPumps(await getHeatPumps().catch(() => []));
        setForm((f) => ({ ...f, heat_pump_id: data.id }));
        toast.success("Heat pump added");
      }
      setAddType(null);
      setAddForm({});
    } catch (e) {
      toast.error("Could not save — are you online?");
    } finally {
      setAddSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Schedule</h1>
          <p className="text-sm text-slate-500">Assign jobs to engineers.</p>
        </div>
        <Button className="h-11" onClick={() => setOpen(true)} data-testid="add-job-btn">
          <CalendarPlus className="mr-1.5 h-4 w-4" /> New job
        </Button>
      </div>

      {jobs.length > 0 && (
        <Card className="border-primary/30 bg-primary/5 p-4" data-testid="todays-route-card">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-900">Day route</p>
              <p className="text-xs text-slate-500">
                {todaysJobs.length} job{todaysJobs.length === 1 ? "" : "s"} on this day
                {todaysJobs.length > 0 && !todaysRouteUrl ? " (no addresses yet)" : ""}
              </p>
            </div>
            <Button
              className="h-11 shrink-0"
              disabled={!todaysRouteUrl}
              onClick={() => todaysRouteUrl && window.open(todaysRouteUrl, "_blank", "noopener")}
              data-testid="todays-route-btn"
            >
              <Route className="mr-1.5 h-4 w-4" /> Open route
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input
              type="date"
              value={routeDate}
              onChange={(e) => setRouteDate(e.target.value)}
              className="h-10 w-auto"
              data-testid="route-date"
            />
            <button
              type="button"
              onClick={() => setRouteDate(todayISO())}
              className="rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              data-testid="route-today-btn"
            >
              Today
            </button>
          </div>
        </Card>
      )}

      <div className="space-y-2.5">
        {jobs.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No jobs scheduled.</p>}
        {sortedJobs.map((j) => (
          <Card key={j.id} className="p-4" data-testid={`job-row-${j.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold text-slate-900">{j.customer_name}</span>
                  <Badge variant="outline" className="text-[10px]">{j.job_type}</Badge>
                  {overlapIds.has(j.id) && (
                    <Badge className="gap-1 bg-amber-100 text-[10px] text-amber-700 hover:bg-amber-100" data-testid={`overlap-${j.id}`}>
                      <AlertTriangle className="h-3 w-3" /> Overlaps
                    </Badge>
                  )}
                </div>
                <div className="mt-1 flex flex-col gap-0.5 text-xs text-slate-500">
                  {j.site_address || j.site_name ? (
                    <MapLink address={j.site_address || j.site_name} testid={`map-job-${j.id}`} />
                  ) : null}
                  <span className="flex items-center gap-1"><User className="h-3 w-3" /> {j.engineer_name || "Unassigned"}</span>
                  <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {new Date(j.scheduled_date).toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}{j.start_time ? ` • ${j.start_time}${endTimeStr(j) ? `–${endTimeStr(j)}` : ""}` : ""}</span>
                </div>
              </div>
              <Button size="icon" variant="ghost" className="text-red-500" onClick={() => remove(j.id)} data-testid={`remove-job-${j.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Schedule a job</DialogTitle>
            <DialogDescription>Assign a customer, site and engineer to a scheduled date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Sel label="Customer" value={form.customer_id} onChange={(v) => setForm({ ...form, customer_id: v, site_id: "", heat_pump_id: "" })} options={customers.map((c) => ({ value: c.id, label: c.name }))} testid="job-customer" onAdd={() => { setAddForm({}); setAddType("customer"); }} addTestid="add-customer-inline" />
            <Sel label="Site" value={form.site_id} onChange={(v) => setForm({ ...form, site_id: v, heat_pump_id: "" })} options={filteredSites.map((s) => ({ value: s.id, label: s.name }))} disabled={!form.customer_id} testid="job-site" onAdd={() => { setAddForm({}); setAddType("site"); }} addTestid="add-site-inline" />
            <Sel label="Heat pump (optional)" value={form.heat_pump_id} onChange={(v) => setForm({ ...form, heat_pump_id: v })} options={filteredPumps.map((p) => ({ value: p.id, label: `${p.manufacturer} ${p.model}` }))} disabled={!form.site_id} testid="job-pump" onAdd={() => { setAddForm({}); setAddType("pump"); }} addTestid="add-pump-inline" />
            <Sel label="Assign engineer" value={form.engineer_id} onChange={(v) => setForm({ ...form, engineer_id: v })} options={engineers.map((e) => ({ value: e.id, label: e.name }))} testid="job-engineer" />
            <Sel label="Job type" value={form.job_type} onChange={(v) => setForm({ ...form, job_type: v })} options={JOB_TYPES.map((t) => ({ value: t, label: t }))} testid="job-type" />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Scheduled date</Label>
                <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="h-11" data-testid="job-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Start time</Label>
                <Input type="time" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="h-11" data-testid="job-start-time" />
              </div>
            </div>
            <Sel
              label={`Expected duration${form.start_time && form.duration_min ? ` (ends ${endTimeStr(form)})` : ""}`}
              value={String(form.duration_min || "")}
              onChange={(v) => setForm({ ...form, duration_min: Number(v) })}
              options={DURATIONS}
              testid="job-duration"
            />
            {formOverlap && (
              <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" data-testid="job-overlap-warning">
                <AlertTriangle className="h-4 w-4 shrink-0" /> This clashes with another job for the same engineer on this day.
              </p>
            )}
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="job-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button className="h-11 w-full" onClick={save} disabled={saving} data-testid="save-job-btn">Schedule job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickAddDialog
        type={addType}
        form={addForm}
        setForm={setAddForm}
        onSave={saveAdd}
        saving={addSaving}
        onClose={() => {
          setAddType(null);
          setAddForm({});
        }}
      />
    </div>
  );
}
